import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, X, Building2, Search, ChevronsUpDown, Check, Globe, Info, ChevronDown } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { STANDARD_INDUSTRIES, INDUSTRY_CATEGORIES, getIndustryLabel } from '../../constants/industries';
import { COMPANY_ROLES } from '../../constants/roles';
import { ScrollArea } from '../ui/scroll-area';
import { getCountryOptions, getCountryName } from '../../constants/countries';
import { planGateFromResponse } from '../../lib/planGate';
import { isAcceptedCompanyLinkedin, LINKEDIN_REFUSAL } from '../../lib/linkedin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Use centralized roles with bilingual labels
const companyRoles = COMPANY_ROLES.map(r => ({
  value: r.id,
  en: r.label,
  ar: r.labelAr
}));

export function AdminCompanyDialog({ open, onOpenChange, company, token, language, onSuccess, onPlanGate }) {
  const isEdit = !!company;
  const isRTL = language === 'ar';
  
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [industryOpen, setIndustryOpen] = useState(false);
  const countryOptions = useMemo(() => getCountryOptions(language), [language]);
  const [formData, setFormData] = useState({
    canonical_name: '',
    website: '',
    country: '',
    linkedin: '',
    email: '',
    roles: [],
    industries: [],
    description: '',
    linked_project_ids: []
  });

  // Fetch projects for selection
  useEffect(() => {
    const fetchProjects = async () => {
      if (!open) return;
      setLoadingProjects(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/intelligence/projects?limit=500`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setProjects(data.data || []);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, [open, token]);

  useEffect(() => {
    if (company) {
      setFormData({
        canonical_name: company.canonical_name || '',
        website: company.website || '',
        country: company.country || '',
        linkedin: company.linkedin || '',
        email: company.email || '',
        roles: company.roles || [],
        industries: company.industries || [],
        description: company.description || '',
        linked_project_ids: company.linked_project_ids || []
      });
    } else {
      setFormData({
        canonical_name: '',
        website: '',
        country: '',
        linkedin: '',
        email: '',
        roles: [],
        industries: [],
        description: '',
        linked_project_ids: []
      });
    }
    setProjectSearch('');
  }, [company, open]);

  const toggleRole = (role) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  };

  const toggleIndustry = (industryId) => {
    setFormData(prev => ({
      ...prev,
      industries: prev.industries.includes(industryId)
        ? prev.industries.filter(i => i !== industryId)
        : [...prev.industries, industryId]
    }));
  };

  const toggleProject = (projectId) => {
    setFormData(prev => ({
      ...prev,
      linked_project_ids: prev.linked_project_ids.includes(projectId)
        ? prev.linked_project_ids.filter(id => id !== projectId)
        : [...prev.linked_project_ids, projectId]
    }));
  };

  const filteredProjects = projects.filter(p => 
    p.canonical_name?.toLowerCase().includes(projectSearch.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.canonical_name.trim()) {
      toast.error(language === 'ar' ? 'اسم الشركة مطلوب' : 'Company name is required');
      return;
    }
    // WO-47 — only the company's LinkedIn page (or a showcase page) is accepted.
    if (!isAcceptedCompanyLinkedin(formData.linkedin)) {
      toast.error(LINKEDIN_REFUSAL[language] || LINKEDIN_REFUSAL.en);
      return;
    }
    
    setLoading(true);
    
    try {
      const url = isEdit 
        ? `${BACKEND_URL}/api/admin/companies/${company._id}`
        : `${BACKEND_URL}/api/admin/companies`;
      
      const method = isEdit ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const pg = await planGateFromResponse(response);
        if (pg && onPlanGate) { onOpenChange(false); onPlanGate(pg); return; }
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to save company');
      }
      
      toast.success(isEdit 
        ? (language === 'ar' ? 'تم تحديث الشركة بنجاح' : 'Company updated successfully')
        : (language === 'ar' ? 'تم إنشاء الشركة بنجاح' : 'Company created successfully')
      );
      
      onSuccess?.();
      onOpenChange(false);
      
    } catch (error) {
      console.error('Error saving company:', error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const txt = {
    title: language === 'ar' ? (isEdit ? 'تعديل الشركة' : 'إنشاء شركة جديدة') : (isEdit ? 'Edit Company' : 'Create Company'),
    subtitle: language === 'ar' ? 'أدخل تفاصيل الشركة' : 'Enter company details',
    nameLabel: language === 'ar' ? 'اسم الشركة' : 'Company Name',
    website: language === 'ar' ? 'الموقع الإلكتروني' : 'Website',
    country: language === 'ar' ? 'الدولة' : 'Country',
    roles: language === 'ar' ? 'الأدوار' : 'Roles',
    industries: language === 'ar' ? 'الصناعات' : 'Industries',
    linkedProjects: language === 'ar' ? 'المشاريع المرتبطة' : 'Linked Projects',
    searchProjects: language === 'ar' ? 'ابحث عن مشاريع...' : 'Search projects...',
    noProjectsSelected: language === 'ar' ? 'لم يتم اختيار مشاريع' : 'No projects selected',
    description: language === 'ar' ? 'الوصف' : 'Description',
    save: language === 'ar' ? 'حفظ' : 'Save',
    cancel: language === 'ar' ? 'إلغاء' : 'Cancel',
    saving: language === 'ar' ? 'جارٍ الحفظ...' : 'Saving...',
    selectCountry: language === 'ar' ? 'اختر الدولة' : 'Select country',
    linkedin: language === 'ar' ? 'لينكد إن' : 'LinkedIn',
    email: language === 'ar' ? 'البريد الإلكتروني' : 'Contact Email'
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-company-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? <Pencil className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            {txt.title}
          </DialogTitle>
          <DialogDescription>{txt.subtitle}</DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Company Name */}
          <div className="space-y-2">
            <Label>{txt.nameLabel} *</Label>
            <Input
              value={formData.canonical_name}
              onChange={(e) => setFormData(prev => ({ ...prev, canonical_name: e.target.value }))}
              placeholder="Enter company name..."
              className="bg-background"
              data-testid="company-name-input"
            />
          </div>
          
          {/* Website and Country */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{txt.website}</Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://..."
                className="bg-background"
                data-testid="company-website-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{txt.country}</Label>
              <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={countryOpen}
                    className="w-full justify-between font-normal bg-background"
                    data-testid="company-country-select"
                  >
                    <span className={`flex items-center gap-2 truncate ${formData.country ? '' : 'text-muted-foreground'}`}>
                      <Globe className="w-4 h-4 shrink-0 text-muted-foreground" />
                      {formData.country ? getCountryName(formData.country, language) : txt.selectCountry}
                    </span>
                    <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                  <Command>
                    <CommandInput placeholder={language === 'ar' ? 'ابحث عن بلد...' : 'Search country...'} data-testid="company-country-search" />
                    <CommandList>
                      <CommandEmpty>{language === 'ar' ? 'لا توجد نتائج' : 'No country found.'}</CommandEmpty>
                      <CommandGroup>
                        {countryOptions.map(c => (
                          <CommandItem
                            key={c.code}
                            value={`${c.name} ${c.code}`}
                            onSelect={() => {
                              setFormData(prev => ({ ...prev, country: c.code }));
                              setCountryOpen(false);
                            }}
                            data-testid={`company-country-option-${c.code}`}
                          >
                            <Check className={`w-4 h-4 mr-2 ${formData.country === c.code ? 'opacity-100' : 'opacity-0'}`} />
                            <span className="flex-1">{c.name}</span>
                            <span className="text-xs text-muted-foreground">{c.code}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                {language === 'ar'
                  ? 'بلد الشركة هو اسم البلد الذي يقع فيه المقر الرئيسي للشركة'
                  : 'The Company Country is the name of the country where the head office of the company is located'}
              </p>
            </div>
          </div>
          
          {/* LinkedIn and Email */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{txt.linkedin}</Label>
              <Input
                value={formData.linkedin}
                onChange={(e) => setFormData(prev => ({ ...prev, linkedin: e.target.value }))}
                placeholder="https://linkedin.com/company/..."
                className="bg-background"
                data-testid="company-linkedin-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{txt.email}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="contact@company.com"
                className="bg-background"
                data-testid="company-email-input"
              />
            </div>
          </div>
          
          {/* Roles */}
          <div className="space-y-2">
            <Label>{txt.roles}</Label>
            <div className="flex flex-wrap gap-4 p-3 border rounded-md bg-background">
              {companyRoles.map(role => (
                <div key={role.value} className="flex items-center gap-2">
                  <Checkbox
                    id={`role-${role.value}`}
                    checked={formData.roles.includes(role.value)}
                    onCheckedChange={() => toggleRole(role.value)}
                    data-testid={`company-role-${role.value}`}
                  />
                  <Label htmlFor={`role-${role.value}`} className="cursor-pointer">
                    {language === 'ar' ? role.ar : role.en}
                  </Label>
                </div>
              ))}
            </div>
          </div>
          
          {/* Industries */}
          <div className="space-y-2">
            <Label>{txt.industries}</Label>
            <Popover open={industryOpen} onOpenChange={setIndustryOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between font-normal h-auto min-h-10 bg-background"
                  data-testid="company-industries-trigger"
                >
                  <div className="flex flex-wrap gap-1 flex-1 text-left">
                    {formData.industries.length > 0 ? (
                      formData.industries.map(id => (
                        <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
                          {getIndustryLabel(id, language)}
                          <X
                            className="w-3 h-3 cursor-pointer hover:text-destructive"
                            onClick={(e) => { e.stopPropagation(); toggleIndustry(id); }}
                          />
                        </span>
                      ))
                    ) : (
                      <span className="text-muted-foreground">{language === 'ar' ? 'اختر الصناعات...' : 'Select industries...'}</span>
                    )}
                  </div>
                  <ChevronDown className="w-4 h-4 opacity-50 shrink-0 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <ScrollArea className="h-[300px]">
                  <div className="p-2">
                    {INDUSTRY_CATEGORIES.map(category => {
                      const categoryIndustries = STANDARD_INDUSTRIES.filter(ind => ind.category === category.id);
                      if (categoryIndustries.length === 0) return null;
                      return (
                        <div key={category.id} className="mb-3">
                          <div className="text-xs font-semibold text-muted-foreground px-2 py-1 bg-muted/50 rounded">
                            {language === 'ar' ? category.ar : category.en}
                          </div>
                          <div className="mt-1 space-y-1">
                            {categoryIndustries.map(industry => (
                              <div
                                key={industry.id}
                                className="flex items-center space-x-2 px-2 py-1.5 hover:bg-muted/50 rounded cursor-pointer"
                                onClick={() => toggleIndustry(industry.id)}
                                data-testid={`company-industry-${industry.id}`}
                              >
                                <Checkbox checked={formData.industries.includes(industry.id)} />
                                <span className="text-sm">{language === 'ar' ? industry.ar : industry.en}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </div>
          
          {/* Description */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{txt.description}</Label>
              <span className={`text-xs ${formData.description.length > 1000 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                {formData.description.length} / 1000
              </span>
            </div>
            <Textarea
              value={formData.description}
              onChange={(e) => {
                if (e.target.value.length <= 1000) {
                  setFormData(prev => ({ ...prev, description: e.target.value }));
                }
              }}
              placeholder="Enter company description..."
              rows={3}
              className="bg-background"
              data-testid="company-description-input"
            />
            <p className="text-xs text-muted-foreground">
              {language === 'ar' ? 'الحد الأقصى 1000 حرف' : 'Maximum 1000 characters'}
            </p>
          </div>
          
          {/* Linked Projects */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              {txt.linkedProjects}
            </Label>
            
            {/* Selected Projects */}
            {formData.linked_project_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.linked_project_ids.map(projectId => {
                  const project = projects.find(p => p._id === projectId);
                  return project ? (
                    <Badge key={projectId} variant="default" className="gap-1 bg-blue-500/20 text-blue-700 border-blue-300">
                      {project.canonical_name}
                      <X 
                        className="w-3 h-3 cursor-pointer hover:text-destructive" 
                        onClick={() => toggleProject(projectId)} 
                      />
                    </Badge>
                  ) : null;
                })}
              </div>
            )}
            
            {/* Project Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                placeholder={txt.searchProjects}
                className="pl-9 bg-background"
              />
            </div>
            
            {/* Project List */}
            <div className="border rounded-md max-h-40 overflow-y-auto">
              {loadingProjects ? (
                <div className="p-4 text-center text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                </div>
              ) : filteredProjects.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  No projects found
                </div>
              ) : (
                filteredProjects.slice(0, 20).map(project => (
                  <div 
                    key={project._id}
                    className="flex items-center gap-3 p-2 hover:bg-muted/50 cursor-pointer border-b last:border-b-0"
                    onClick={() => toggleProject(project._id)}
                  >
                    <Checkbox 
                      checked={formData.linked_project_ids.includes(project._id)}
                      onCheckedChange={() => toggleProject(project._id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{project.canonical_name}</div>
                      {project.country && (
                        <div className="text-xs text-muted-foreground">{project.country}</div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            {formData.linked_project_ids.length === 0 && (
              <p className="text-xs text-muted-foreground">{txt.noProjectsSelected}</p>
            )}
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              {txt.cancel}
            </Button>
            <Button type="submit" disabled={loading} data-testid="company-save-btn">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {loading ? txt.saving : txt.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AdminCompanyDialog;
